import type { CaseCategoryTone } from './caseCategory';
import type { Case, Column, ConfidenceFields, Direction, GroupId } from './legalCasesType';
import styles from './LegalCases.module.scss';
import { GRACE_WINDOW_MS } from '../../../../../common/hooks/useGraceWindow';
import { formatLacCr } from '../../../../../utils/formatters/formatLacCr';

export const STATUS = {
    PENDING: { value: 'Pending' },
    DISPOSED: { value: 'Disposed' },
    NOT_AVAILABLE: { value: 'Not Available' },
};

export const ROW_MOTION_MS = 220;

export const ROW_MOTION_MAX_ROWS = 25;

// Litigation-tracker polling ladder (see useLegalCasesPolling).
export const LEGAL_CASES_FETCHING_TEXT = 'Fetching Legal Cases Data';
export const LEGAL_CASES_POLL_INTERVAL_MS = 5000;
// 24 attempts x 5s = 120s pending budget — the BE ingest window before the FE gives up.
export const LEGAL_CASES_PENDING_ATTEMPTS = 24;
// Defensive ceiling on the post-COMPLETE grace poll — the GRACE_WINDOW_MS timeout is the real
// terminator; derived from the window over the poll interval, plus one tick of slack, so it
// always comfortably outlasts the window. Evaluates to 13.
export const LEGAL_CASES_GRACE_ATTEMPTS = Math.ceil(GRACE_WINDOW_MS / LEGAL_CASES_POLL_INTERVAL_MS) + 1;

// Written as not-LOW, not as a `=== 'HIGH'` whitelist, because it has to be the literal mirror of
// polus's LegalCasesSummaryUtil.isConfirmed (`!CONFIDENCE_LOW.equals(...)`). A HIGH whitelist and
// a not-LOW check are complementary, not equivalent — they only agree on today's three values
// (HIGH/LOW/absent); any other level would silently split them, this side calling it Probable
// while polus counts it Confirmed. Don't "simplify" this back to the whitelist form.
export function isHighConfidence(c: ConfidenceFields): boolean {
    return c.confidenceLevel !== 'LOW';
}

export function hasConfidenceSignal(cases: ConfidenceFields[]): boolean {
    return cases.some((c) => c.confidenceLevel != null);
}

export const HEADERS = {
    FILED_BY: 'filedBy',
    FILED_AGAINST: 'filedAgainst',
    CASE_CATEGORY: 'caseCategory',
    COURT: 'court',
    STATUS: 'status',
    CASE_NUM: 'caseNum',
    DATE: 'date',
};
const HEADER_LABELS = {
    [HEADERS.FILED_BY]: 'Filed By',
    [HEADERS.FILED_AGAINST]: 'Filed Against',
    [HEADERS.CASE_CATEGORY]: 'Case Type',
    [HEADERS.COURT]: 'Court',
    [HEADERS.STATUS]: 'Status',
    [HEADERS.CASE_NUM]: 'Case No.',
    [HEADERS.DATE]: 'Date',
};

const HEADER_LABELS_STAR = {
    [HEADERS.FILED_BY]: 'Filed By*',
    [HEADERS.FILED_AGAINST]: 'Filed Against*',
};

export const CASE_TYPE_TONE_CLASS: Record<CaseCategoryTone, string> = {
    civil: styles.CaseType_Civil,
    criminal: styles.CaseType_Criminal,
    neutral: styles.CaseType_Neutral,
};

export const GROUP_BADGE_CLASS: Record<GroupId, string> = {
    OPEN: styles.CaseGroupBadge_Open,
    DISPOSED: styles.CaseGroupBadge_Disposed,
    OTHER: styles.CaseGroupBadge_Other,
};

export const COMPANY_NAME_PATTERN =
    /\b(ltd|limited|pvt|private|llp|inc|corp|corporation|company|industries|enterprises|traders|electricals|technologies|solutions|bank|government|authority|department|commissioner|ministry|board|insurance|agencies|products|pharma|textiles|exports|imports|motors|steels|associates|m\/s)\b|\bn\.a\.|& co\b/i;

export function getColumns(direction: Direction): Column[] {
    const [primary, secondary] =
        direction === 'filed_against' ? [HEADERS.FILED_AGAINST, HEADERS.FILED_BY] : [HEADERS.FILED_BY, HEADERS.FILED_AGAINST];

    return [
        { key: HEADERS.CASE_NUM, label: HEADER_LABELS[HEADERS.CASE_NUM] },
        { key: primary, label: HEADER_LABELS_STAR[primary] },
        { key: secondary, label: HEADER_LABELS[secondary] },
        { key: HEADERS.CASE_CATEGORY, label: HEADER_LABELS[HEADERS.CASE_CATEGORY] },
        { key: HEADERS.COURT, label: HEADER_LABELS[HEADERS.COURT] },
        { key: HEADERS.DATE, label: HEADER_LABELS[HEADERS.DATE] },
    ];
}

const RECORD_TYPE_DEFAULTER = 'DEFAULTER';

export const DEFAULTER_EMPTY_VALUE = '—';

export const isDefaulterCase = (c?: { recordType?: string } | null): boolean => c?.recordType === RECORD_TYPE_DEFAULTER;

export const formatDefaulterAmount = (amount?: number | null): string =>
    amount == null || !Number.isFinite(amount) ? '' : `₹${formatLacCr(amount)}`;

/** "Defaulter - Suit Filed (₹22.67 Cr)" — the one label the Excel export and the V2 PDF share. */
export function formatDefaulterCaseType(standardCaseType?: string | null, amount?: number | null): string {
    const caseType = standardCaseType?.trim() || DEFAULTER_EMPTY_VALUE;
    const amountText = formatDefaulterAmount(amount);
    return amountText ? `${caseType} (${amountText})` : caseType;
}

const DEFAULTER_CLOSED_STATUSES = ['disposed', 'settled', 'closed'];

/** Mirrors polus LegalCasesSummaryUtil.isOpen: exact "Disposed" for every row, plus the defaulter-only closed set. */
export const isClosedLegalCase = (c: Pick<Case, 'status' | 'recordType'>): boolean =>
    c.status === STATUS.DISPOSED.value || (isDefaulterCase(c) && DEFAULTER_CLOSED_STATUSES.includes(c.status?.trim().toLowerCase() ?? ''));

/** The two Legal Cases directions, as the Filed Against / Filed By toggle names them. */
export const LEGAL_DIRECTION = {
    FILED_AGAINST: 'filed_against',
    FILED_BY: 'filed_by',
} as const;

/** Deep-link param naming which direction a Legal Cases click came from (Filed Against is the default). */
export const LEGAL_DIRECTION_PARAM = 'legalDirection';
